'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { useTheme } from 'next-themes';
import {
  Buildings,
  Lifebuoy,
  Lightning,
  ChatCircle,
  CaretRight,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { OPEN_HOUR, CLOSE_HOUR } from '@/lib/business-hours';

interface FooterProps {
  className?: string;
}

const footerLinks = {
  company: {
    title: 'Компания',
    icon: Buildings,
    links: [
      { label: 'Вакансии', href: '/vacancies' },
      { label: 'Арендодателям', href: '/rent' },
    ],
  },
  service: {
    title: 'Сервис',
    icon: Lightning,
    links: [
      { label: 'Доставка', href: '/delivery' },
      { label: 'Оплата', href: '/payment' },
    ],
  },
  help: {
    title: 'Помощь',
    icon: Lifebuoy,
    links: [
      { label: 'FAQ', href: '/faq' },
      { label: 'Аллергены', href: '/allergens' },
      { label: 'Блог', href: '/blog' },
    ],
  },
};

const VKIcon = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className || 'w-6 h-6'} style={style}>
    <path d="M15.07 2H8.93C3.33 2 2 3.33 2 8.93v6.14C2 20.67 3.33 22 8.93 22h6.14c5.6 0 6.93-1.33 6.93-6.93V8.93C22 3.33 20.67 2 15.07 2m3.08 14.27h-1.46c-.55 0-.72-.44-1.71-1.45-1-.97-1.43-1.1-1.68-1.1-.35 0-.45.1-.45.58v1.31c0 .41-.13.66-1.21.66-1.79 0-3.78-.97-5.18-2.77-2.1-2.95-2.67-5.16-2.67-5.61 0-.25.1-.48.58-.48h1.46c.44 0 .6.19.77.65.85 2.45 2.27 4.6 2.85 4.6.22 0 .32-.1.32-.66V9.13c-.07-1.14-.67-1.24-.67-1.65 0-.2.17-.41.44-.41h2.3c.37 0 .5.19.5.63v3.41c0 .37.17.5.27.5.22 0 .4-.13.81-.54 1.25-1.4 2.14-3.56 2.14-3.56.12-.25.32-.48.77-.48h1.46c.44 0 .54.23.44.63-.19.87-2.03 3.47-2.03 3.47-.16.26-.22.38 0 .67.16.22.69.67 1.04 1.08.65.72 1.15 1.33 1.28 1.75.14.41-.09.62-.52.62"/>
  </svg>
);

const MaxIcon = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <svg viewBox="-100 -100 960 958" fill="currentColor" className={className} style={style}>
    <path fillRule="evenodd" clipRule="evenodd" d="M392.07 186.804C292.648 181.591 215.063 250.579 197.954 358.557C183.786 447.977 208.906 556.935 230.392 562.419C239.505 564.745 261.436 547.971 277.392 532.925C280.391 530.096 284.927 529.62 288.445 531.771C313.317 546.98 341.477 558.41 372.522 560.038C474.59 565.39 565.042 485.507 570.388 383.43C575.735 281.353 494.139 192.157 392.07 186.804ZM225.581 705.191C221.732 702.466 216.428 703.205 213.202 706.646C170.082 752.636 59.7277 784.896 54.6727 722.126C54.6727 672.946 43.6266 631.503 31.4646 585.873C16.5692 529.989 0 467.824 0 377.475C0 162.027 176.694 0 386.194 0C595.694 0 759.951 169.938 759.951 379.567C759.951 589.196 590.466 754.95 388.176 754.95C316.409 754.95 281.583 744.841 225.581 705.191Z" />
  </svg>
);

const socialLinks = [
  { name: 'VK', Icon: VKIcon, href: 'https://vk.com/aziatochka_ru' },
];

const legalLinks = [
  { label: 'Политика конфиденциальности', href: '/privacy' },
  { label: 'Публичная оферта', href: '/oferta' },
  { label: 'Безопасные платежи', href: '/secure-payments' },
  { label: 'Политика возврата', href: '/refund' },
];

const whiteText = { color: '#FFFFFF' };
const whiteText70 = { color: 'rgba(255, 255, 255, 0.7)' };
const whiteText60 = { color: 'rgba(255, 255, 255, 0.6)' };

/* ─── Glass card reusable ─── */
const glassCard = (isDark: boolean) =>
  cn(
    'backdrop-blur-sm rounded-card md:rounded-screen p-4 md:p-5 transition-all duration-300 hover:scale-[1.02] hover:shadow-float',
    isDark
      ? 'bg-white/5 border border-white/10 hover:bg-white/10'
      : 'bg-white/10 hover:bg-white/15',
  );

export function Footer({ className }: FooterProps) {
  const [mounted, setMounted] = useState(false);
  const { resolvedTheme } = useTheme();

  useEffect(() => { setMounted(true); }, []);

  const isDark = mounted ? resolvedTheme === 'dark' : false;

  const phoneNumber = '8 (863) 333-50-50';
  // Часы приёма заказов — из lib/business-hours.ts, а не строкой: это то же
  // значение, что уходит в разметку для Google (вопрос 46, 11:00–22:00).
  // Импортируем константы, а не siteConfig: тот тянет весь SEO-конфиг с
  // районами и ключевыми словами в клиентский бандл, а футер есть на каждой
  // странице.
  const workingHours = `${OPEN_HOUR}:00 - ${CLOSE_HOUR}:00`;

  return (
    <footer className={cn('relative bg-[var(--bg-primary)]', className)}>
      {/* ── Career Banner ── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <motion.div
          className={cn(
            'relative overflow-hidden rounded-card md:rounded-screen',
            isDark
              ? 'bg-[var(--surface)] border border-[var(--border-default)]'
              : 'bg-[var(--primary)]',
          )}
          whileHover={{ scale: 1.01 }}
          transition={{ duration: 0.2 }}
        >
          {/* Dot pattern */}
          <div className={cn('absolute inset-0', isDark ? 'opacity-5' : 'opacity-20')}>
            <svg width="100%" height="100%">
              <defs>
                <pattern id="dots-career" x="0" y="0" width="12" height="12" patternUnits="userSpaceOnUse">
                  <circle cx="2" cy="2" r="1" fill="white" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#dots-career)" />
            </svg>
          </div>

          <div className="relative flex items-center justify-between px-4 py-3 md:px-8 md:py-6 gap-4">
            <span className="text-sm md:text-xl font-semibold" style={whiteText}>
              Стань частью команды Aziatochka
            </span>
            <Link
              href="/vacancies"
              className={cn(
                'flex-shrink-0 flex items-center gap-2 px-4 py-1.5 md:px-8 md:py-3 rounded-pill text-xs md:text-base font-medium transition-all duration-300',
                // hover:shadow-lg + hover:shadow-white/25 ниже ОСТАВЛЕНЫ намеренно.
                // Это не мёртвая запись и не системная глубина: shadow-white/25 —
                // настоящий цвет тени (в отличие от shadow-[var(--primary)]/N,
                // который ничего не красил), и на фиолетовом баннере он даёт белый
                // ореол вокруг белой кнопки. Замена на shadow-float поменяла бы
                // белое свечение на тёмную тень, то есть молча убрала работающий
                // эффект, которого никто не просил. Роли «белое свечение» в
                // системе нет: shadow-glow — фиолетовый.
                isDark
                  ? 'bg-[var(--violet-500)] hover:bg-[var(--violet-400)]'
                  : 'bg-white/95 hover:bg-white hover:shadow-lg hover:shadow-white/25',
              )}
              style={{ color: isDark ? '#FFFFFF' : 'var(--violet-700)' }}
            >
              Карьера
              <CaretRight className="h-4 w-4 hidden md:block" weight="bold" />
            </Link>
          </div>
        </motion.div>
      </div>

      {/* ── Main Footer Block ── */}
      <div className="max-w-7xl mx-auto px-0 sm:px-6">
        <div className={cn(
          // Купол футера — «экран внутри экрана», ступень screen (32).
          // Было 30 на мобильном и 40 на десктопе; пятой ступени под 40 в
          // системе нет и владелец её отклонил, поэтому обе сводятся к 32.
          'relative overflow-hidden rounded-t-screen',
          isDark
            ? 'bg-[var(--surface)] border border-[var(--border-default)]'
            : 'bg-[var(--primary)]',
        )}>
          {/* Decorative blobs */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {isDark ? (
              <>
                <div className="blur-blob absolute top-0 left-1/4 w-[400px] h-[400px] bg-[var(--violet-500)]/10 rounded-pill blur-[120px]" />
                <div className="blur-blob absolute bottom-0 right-1/4 w-[300px] h-[300px] bg-[var(--violet-400)]/5 rounded-pill blur-[100px]" />
              </>
            ) : (
              <>
                <div className="absolute top-0 left-0 w-32 h-32 bg-white/5 rounded-pill -translate-x-16 -translate-y-16" />
                <div className="absolute top-20 right-0 w-24 h-24 bg-white/10 rounded-pill translate-x-12" />
                <div className="absolute bottom-16 left-1/4 w-16 h-16 bg-white/5 rounded-pill" />
              </>
            )}
          </div>

          <div className="relative px-4 md:px-6 lg:px-8 pt-6 md:pt-12 pb-6 md:pb-8">

            {/* ─── DESKTOP: 2 rows of 3 cards ─── */}
            <div className="hidden md:block space-y-6 mb-10">
              {/* Row 1: Company / Service / Help */}
              <div className="grid grid-cols-3 gap-6">
                {Object.values(footerLinks).map((section) => (
                  <div key={section.title} className={glassCard(isDark)}>
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold text-lg" style={whiteText}>{section.title}</h4>
                      <section.icon className="h-6 w-6" weight="bold" style={isDark ? { color: 'var(--violet-400)' } : whiteText} />
                    </div>
                    <div className="w-full h-px bg-white/30 mb-4" />
                    <ul className="space-y-3">
                      {section.links.map((link) => (
                        <li key={link.href}>
                            <Link href={link.href} className="text-sm transition-colors hover:!text-white" style={whiteText70}>
                              {link.label}
                            </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              {/* Row 2: Reviews / Phone number */}
              <div className="grid grid-cols-2 gap-6">
                {/* Reviews */}
                <div className={glassCard(isDark)}>
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-lg" style={whiteText}>Отзывы</h4>
                    <ChatCircle className="h-6 w-6" weight="bold" style={isDark ? { color: 'var(--violet-400)' } : whiteText} />
                  </div>
                  <div className="w-full h-px bg-white/30 mb-4" />
                  <Link
                    href="/reviews"
                    className={cn(
                      'inline-block px-4 py-2 rounded-pill text-sm font-medium transition-colors',
                      isDark ? 'bg-[var(--violet-500)]/20 hover:bg-[var(--violet-500)]/30' : 'bg-white/30 hover:bg-white/40',
                    )}
                    style={isDark ? { color: 'var(--violet-300)' } : whiteText}
                  >
                    Смотреть отзывы
                  </Link>
                </div>

                {/* Phone number */}
                <div className={cn(glassCard(isDark), 'flex items-center justify-between')}>
                  <div>
                    <p className="text-xs uppercase tracking-wider font-medium mb-1" style={whiteText60}>Поддержка</p>
                    <a
                      href={`tel:${phoneNumber.replace(/\D/g, '')}`}
                      className="block font-bold text-xl"
                      style={whiteText}
                    >
                      <span>{phoneNumber}</span>
                    </a>
                  </div>
                  <div className="flex gap-3">
                    <a href="https://max.ru/u/f9LHodD0cOJy8oPBxUr-x-mis-JgEXUn4KSVirJM9Ix_OdGwXP4A3anpCCU" target="_blank" rel="noopener noreferrer" className={cn('w-11 h-11 rounded-pill flex items-center justify-center transition-colors', isDark ? 'bg-white/10 hover:bg-white/20' : 'bg-white/20 hover:bg-white/30')} style={whiteText}>
                      <MaxIcon className="w-5 h-5" />
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* ─── MOBILE: 2 columns masonry-style ─── */}
            <div className="md:hidden flex gap-3 mb-6">
              {/* Left column */}
              <div className="flex-1 space-y-3">
                {/* Company */}
                <div className={glassCard(isDark)}>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-sm" style={whiteText}>{footerLinks.company.title}</h4>
                    <footerLinks.company.icon className="h-5 w-5" weight="bold" style={isDark ? { color: 'var(--violet-400)' } : whiteText} />
                  </div>
                  <div className="w-full h-px bg-white/30 mb-3" />
                  <ul className="space-y-2">
                    {footerLinks.company.links.map((link) => (
                      <li key={link.href}>
                        <Link href={link.href} className="text-xs transition-colors hover:!text-white" style={whiteText70}>{link.label}</Link>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Help */}
                <div className={glassCard(isDark)}>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-sm" style={whiteText}>{footerLinks.help.title}</h4>
                    <footerLinks.help.icon className="h-5 w-5" weight="bold" style={isDark ? { color: 'var(--violet-400)' } : whiteText} />
                  </div>
                  <div className="w-full h-px bg-white/30 mb-3" />
                  <ul className="space-y-2">
                    {footerLinks.help.links.map((link) => (
                      <li key={link.href}>
                          <Link href={link.href} className="text-xs transition-colors hover:!text-white" style={whiteText70}>{link.label}</Link>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Phone */}
              </div>

              {/* Right column */}
              <div className="flex-1 space-y-3">
                {/* Service */}
                <div className={glassCard(isDark)}>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-sm" style={whiteText}>{footerLinks.service.title}</h4>
                    <footerLinks.service.icon className="h-5 w-5" weight="bold" style={isDark ? { color: 'var(--violet-400)' } : whiteText} />
                  </div>
                  <div className="w-full h-px bg-white/30 mb-3" />
                  <ul className="space-y-2">
                    {footerLinks.service.links.map((link) => (
                      <li key={link.href}>
                        <Link href={link.href} className="text-xs transition-colors hover:!text-white" style={whiteText70}>{link.label}</Link>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Reviews */}
                <div className={glassCard(isDark)}>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-sm" style={whiteText}>Отзывы</h4>
                    <ChatCircle className="h-5 w-5" weight="bold" style={isDark ? { color: 'var(--violet-400)' } : whiteText} />
                  </div>
                  <div className="w-full h-px bg-white/30 mb-3" />
                  <ul className="space-y-2">
                    <li>
                      <Link href="/reviews" className="text-xs transition-colors hover:!text-white" style={whiteText70}>Все отзывы</Link>
                    </li>
                    <li>
                      <Link href="/reviews" className="text-xs transition-colors hover:!text-white" style={whiteText70}>Оставить отзыв</Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Phone - full width horizontal on mobile */}
            <div className="md:hidden mb-6">
              <div className={cn(glassCard(isDark), 'flex items-center justify-between')}>
                <div>
                  <p className="t-nano uppercase tracking-wider font-medium mb-1" style={whiteText60}>Поддержка</p>
                  <a
                    href={`tel:${phoneNumber.replace(/\D/g, '')}`}
                    className="font-bold text-base"
                    style={whiteText}
                  >
                    <span>{phoneNumber}</span>
                  </a>
                </div>
                <div className="flex gap-2">
                  <a href="https://max.ru/u/f9LHodD0cOJy8oPBxUr-x-mis-JgEXUn4KSVirJM9Ix_OdGwXP4A3anpCCU" target="_blank" rel="noopener noreferrer" className={cn('w-9 h-9 rounded-pill flex items-center justify-center', isDark ? 'bg-white/10' : 'bg-white/20')} style={whiteText}>
                    <MaxIcon className="w-4.5 h-4.5" />
                  </a>
                </div>
              </div>
            </div>

            {/* ── Divider ── */}
            <div className="h-px bg-white/20 mb-6" />

            {/* ── Social section ── */}
            <div className="flex items-start justify-between mb-6">
              <div className="flex-1 pr-4">
                <h4 className="font-semibold text-base md:text-2xl leading-tight mb-2" style={whiteText}>
                  <span className="md:hidden">Наши<br />социальные сети</span>
                  <span className="hidden md:inline">Наши социальные сети</span>
                </h4>
                <p className="text-xs md:text-base" style={whiteText70}>
                  Будь в курсе всех новостей!
                </p>
              </div>
              <div className="flex gap-2 md:gap-4">
                {socialLinks.map((social) => (
                  <motion.a
                    key={social.name}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      'flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-pill backdrop-blur-sm transition-colors',
                      isDark ? 'bg-white/10 hover:bg-white/20' : 'bg-white/20 hover:bg-white/30',
                    )}
                    style={whiteText}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    title={social.name}
                  >
                    <social.Icon className="h-5 w-5" />
                  </motion.a>
                ))}
              </div>
            </div>

            {/* ── Divider ── */}
            <div className="h-px bg-white/20 mb-4 md:mb-6" />

            {/* ── Legal ── */}
            {/* Desktop: 3 columns */}
            <div className="hidden md:grid grid-cols-3 gap-8">
              <div className="space-y-3">
                {legalLinks.slice(0, 2).map((link) => (
                  <Link key={link.href} href={link.href} className="block text-sm transition-colors hover:!text-white" style={whiteText70}>
                    {link.label}
                  </Link>
                ))}
                <span className="block text-base font-medium" style={whiteText}>
                  Aziatochka.ru <span suppressHydrationWarning>{new Date().getFullYear()}</span>
                </span>
              </div>
              <div className="space-y-3">
                {legalLinks.slice(2).map((link) => (
                  <Link key={link.href} href={link.href} className="block text-sm transition-colors hover:!text-white" style={whiteText70}>
                    {link.label}
                  </Link>
                ))}
              </div>
              <div className="flex items-end justify-end">
                <span className="text-sm" style={whiteText70}>Все права защищены</span>
              </div>
            </div>

            {/* Mobile: 2 columns */}
            <div className="md:hidden grid grid-cols-2 gap-3">
              <div className="space-y-3">
                {legalLinks.slice(0, 2).map((link) => (
                  <Link key={link.href} href={link.href} className="block t-micro font-normal transition-colors hover:!text-white" style={whiteText70}>
                    {link.label}
                  </Link>
                ))}
                <span className="block text-xs font-medium" style={whiteText}>
                  Aziatochka.ru <span suppressHydrationWarning>{new Date().getFullYear()}</span>
                </span>
              </div>
              <div className="space-y-3 text-right">
                {legalLinks.slice(2).map((link) => (
                  <Link key={link.href} href={link.href} className="block t-micro font-normal transition-colors hover:!text-white" style={whiteText70}>
                    {link.label}
                  </Link>
                ))}
                <span className="block t-micro font-normal" style={whiteText70}>Все права защищены</span>
              </div>
            </div>

          </div>
        </div>
      </div>

    </footer>
  );
}
